import React, { useEffect, useState } from 'react';
import { 
    Box, 
    Typography, 
    Container, 
    Button, 
    Stack, 
    CircularProgress,
    useTheme,
    Snackbar,
    Alert,
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    TextField, 
    MenuItem, 
    Grid
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import AdmissionsPipeline from '../components/crm/AdmissionsPipeline';
import careService from '../services/careService';
import { ProspectiveResidentDTO, AdmissionStatus } from '../types/crm.types';

const AdmissionInquiryPage: React.FC = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [prospects, setProspects] = useState<ProspectiveResidentDTO[]>([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
    
    // Dialog States
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [selectedProspect, setSelectedProspect] = useState<ProspectiveResidentDTO | null>(null);
    
    // Form States
    const [formData, setFormData] = useState<Partial<ProspectiveResidentDTO>>({
        name: '',
        responsibleParty: '',
        email: '',
        phone: '',
        source: 'Website',
        status: AdmissionStatus.INQUIRY,
        notes: '',
        readinessScore: 0
    });

    useEffect(() => {
        fetchProspects();
    }, []);

    const fetchProspects = async () => {
        setLoading(true);
        try {
            const data = await careService.getLeads();
            setProspects(data);
        } catch (error) {
            console.error('Failed to fetch prospective residents', error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenCreate = () => {
        setFormData({
            name: '',
            responsibleParty: '',
            email: '',
            phone: '',
            source: 'Website',
            status: AdmissionStatus.INQUIRY,
            notes: '',
            readinessScore: 0
        });
        setIsCreateOpen(true);
    };

    const handleOpenEdit = (prospect: ProspectiveResidentDTO) => {
        setSelectedProspect(prospect);
        setFormData(prospect);
        setIsEditOpen(true);
    };

    const handleCreate = async () => {
        try {
            await careService.createLead(formData);
            showSnackbar('Inquiry recorded successfully', 'success');
            setIsCreateOpen(false);
            fetchProspects();
        } catch (error) {
            showSnackbar('Failed to record inquiry', 'error');
        }
    };

    const handleUpdate = async () => {
        if (!selectedProspect) return;
        try {
            await careService.updateLead(selectedProspect.id, formData);
            showSnackbar('Prospective record updated', 'success');
            setIsEditOpen(false);
            fetchProspects();
        } catch (error) {
            showSnackbar('Failed to update record', 'error');
        }
    };

    const handleStatusChange = async (prospectId: string, newStatus: AdmissionStatus) => {
        try {
            await careService.updateLeadStatus(prospectId, newStatus);
            showSnackbar(`Applicant moved to ${newStatus}`, 'success');
            fetchProspects();
        } catch (error) {
            showSnackbar('Failed to move applicant', 'error');
        }
    };

    const showSnackbar = (message: string, severity: 'success' | 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    return (
        <Container maxWidth={false} sx={{ py: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
                <Box>
                    <Typography variant="h4" fontWeight="800" color="text.primary">
                        Admissions Management
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Track your admissions funnel and prospective unit residents.
                    </Typography>
                </Box>
                <Button 
                    variant="contained" 
                    startIcon={<AddIcon />} 
                    onClick={handleOpenCreate}
                    sx={{ borderRadius: 2, fontWeight: '700' }}
                >
                    Add Admission Inquiry
                </Button>
            </Box>

            {loading ? (
                <Box display="flex" justifyContent="center" py={10}>
                    <CircularProgress />
                </Box>
            ) : (
                <AdmissionsPipeline 
                    leads={prospects} 
                    onLeadClick={handleOpenEdit} 
                    onStatusChange={handleStatusChange}
                />
            )}

            {/* Create Inquiry Dialog */}
            <Dialog open={isCreateOpen} onClose={() => setIsCreateOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: '800' }}>New Admission Inquiry</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Applicant Name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Responsible Party / Guardian" value={formData.responsibleParty} onChange={(e) => setFormData({...formData, responsibleParty: e.target.value})} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Contact Email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Contact Phone" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth select label="Inquiry Source" value={formData.source} onChange={(e) => setFormData({...formData, source: e.target.value})}>
                                <MenuItem value="Website">Website</MenuItem>
                                <MenuItem value="Referral">Referral</MenuItem>
                                <MenuItem value="Hospital Discharge">Hospital Discharge</MenuItem>
                                <MenuItem value="Social Media">Social Media</MenuItem>
                                <MenuItem value="Walk-in">Walk-in</MenuItem>
                            </TextField>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setIsCreateOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreate}>Record Inquiry</Button>
                </DialogActions>
            </Dialog>

            {/* Edit Prospect Dialog */}
            <Dialog open={isEditOpen} onClose={() => setIsEditOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: '800' }}>Inquiry Details</DialogTitle>
                <DialogContent>
                    <Grid container spacing={3} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={6}>
                            <Stack spacing={2}>
                                <TextField fullWidth label="Applicant Name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
                                <TextField fullWidth label="Responsible Party" value={formData.responsibleParty} onChange={(e) => setFormData({...formData, responsibleParty: e.target.value})} />
                                <TextField fullWidth label="Email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
                                <TextField fullWidth label="Phone" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Stack spacing={2}>
                                <TextField fullWidth select label="Admission Phase" value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value as AdmissionStatus})}>
                                    {Object.values(AdmissionStatus).map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                </TextField>
                                <TextField fullWidth type="number" label="Readiness Score (1-100)" value={formData.readinessScore} onChange={(e) => setFormData({...formData, readinessScore: Number(e.target.value)})} />
                                <TextField fullWidth multiline rows={4} label="Admission Notes / Clinical Context" value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} />
                            </Stack>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setIsEditOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleUpdate}>Save Profile</Button>
                </DialogActions>
            </Dialog>

            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={4000} 
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2, fontWeight: '700' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default AdmissionInquiryPage;
